/**
 * Files on the clipboard, from whichever of the two surfaces a browser fills in.
 *
 * `clipboardData.files` is empty for some copy sources — a screenshot copied out
 * of an image viewer, a file copied in a file manager on some platforms, an image
 * copied from another app on iPadOS — while the very same file is reachable as a
 * `DataTransferItem` of kind 'file'. The paste handler used to read `.files` only,
 * so those pastes fell through to the text branch and nothing was attached
 * (chat-thread PR-6, 2026-09-09).
 *
 * ONE SURFACE WINS — reading both is double-counting (2026-09-11). Miles, on the
 * live build: "when I paste a photo in one time (maybe other files) it appears
 * twice in the draft message section". The first cut read BOTH lists and leaned on
 * an identity key to drop the repeat, which is backwards: per the HTML drag-and-
 * drop model these are two VIEWS OF THE SAME STORE — `.files` is exactly the
 * kind-'file' items — so the same payload is always on both, and the key only has
 * to miss once for a paste to double. It misses routinely: `getAsFile()` MINTS a
 * File at the moment of the call, so a pasted screenshot's two copies carry
 * different `lastModified` stamps (and, on some builds, different generated names)
 * while being the same picture. Hence 2 identical 382 KB cards from one ⌘V.
 *
 * So `.files` is read as the whole answer when it holds anything, and the items
 * are read only when it is empty — which is the ONLY case PR-6 was ever about.
 * A paste that worked before behaves byte for byte as it did; a paste that
 * attached nothing still gets its file; neither can produce a duplicate, whatever
 * `getAsFile()` decides to stamp on it.
 */

/** The subset of DataTransfer the reader needs — a plain object works in tests. */
export interface ClipboardDataLike {
  files?: ArrayLike<File | null | undefined> | null;
  items?: ArrayLike<{ kind?: string; getAsFile?: () => File | null } | null | undefined> | null;
}

function identityOf(f: File): string {
  return `${f.name}|${f.size}|${f.type}|${typeof f.lastModified === 'number' ? f.lastModified : ''}`;
}

/** Collects into `out`, skipping anything already there by identity. */
function collect(out: File[], seen: Set<string>, f: File | null | undefined): void {
  if (!f || typeof f !== 'object') return;
  const key = identityOf(f);
  if (seen.has(key)) return;
  seen.add(key);
  out.push(f);
}

export function filesFromClipboard(data: ClipboardDataLike | null | undefined): File[] {
  if (!data) return [];
  const out: File[] = [];
  const seen = new Set<string>();

  const files = data.files;
  if (files && typeof files.length === 'number') {
    for (let i = 0; i < files.length; i += 1) collect(out, seen, files[i]);
  }
  // The fallback, and ONLY the fallback: anything `.files` listed is the whole
  // answer, so re-reading the same store through the items would duplicate it.
  if (out.length > 0) return out;

  const items = data.items;
  if (items && typeof items.length === 'number') {
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (!item || item.kind !== 'file' || typeof item.getAsFile !== 'function') continue;
      try {
        collect(out, seen, item.getAsFile());
      } catch {
        // a sandboxed / already-consumed item — nothing to attach from it
      }
    }
  }
  return out;
}
